import { CoreButtonView, CoreTextView, type TCoreButtonStyleProps, type TCoreButtonViewProps } from '@core/widgets';
import { AssetsSvgMap } from '@libs/types';
import React from 'react';
import { getSemanticColors, memoByPalette, SemanticColor, TSemanticColors } from '../../theme/colors';
import { ButtonCircleView, type TButtonCircleViewProps } from '../Buttons/ButtonCircleView';
import { getHierarchyBgMap } from '../Buttons/styles/buttonPillUtils';
import styles from './passcodeKeypad.module.css';

const KEY_SIZE = 56;
const ICON_SIZE = 24;

/** `null` — empty slot (taken by the biometry button when it is available), `backspace` — delete the last digit. */
type TKeypadKey = string | 'backspace' | null;

const KEYPAD_ROWS: TKeypadKey[][] = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    [null, '0', 'backspace'],
];

const buildDigitStyleProps = (colors: TSemanticColors) => {
    const button: TCoreButtonStyleProps = {
        ...getHierarchyBgMap(colors).secondary,
        width: KEY_SIZE,
        height: KEY_SIZE,
        borderRadius: KEY_SIZE / 2,
        padding: { top: 0, right: 0, bottom: 0, left: 0 },
        // Same token as the raised ButtonCircleView keys (backspace / biometry): one elevation for the whole keypad,
        // and it is defined in both the consumer and the lulox theme
        boxShadow: 'var(--shadow_grey)',
        cursorDisabled: 'default',
    };
    const text = {
        main: colors[SemanticColor.TextPrimary],
        font: 'var(--font-main)',
        fontStyle: { fontSize: 20, lineHeight: 20, letterSpacing: -0.5 },
        disabled: { main: colors[SemanticColor.TextPrimaryDisabled] },
    };
    return { button, text };
};
const getDigitStyleProps = memoByPalette(buildDigitStyleProps);

export type TPasscodeKeypadViewProps = {
    /** Design version of the passcode widget (`TWidget.version`). */
    version: number | undefined;
    uid: string;
    onDigit: (digit: string) => void;
    onBackspace: () => void;
    /** Accessible name of the icon-only backspace key. */
    backspaceLabel?: string;
    /** Shows the biometry button in the empty bottom-left slot. */
    showBiometry?: boolean;
    onBiometry?: () => void;
    /** Accessible name of the biometry button (server-driven text). */
    biometryLabel?: string;
    isDigitsDisabled?: boolean;
    isBackspaceDisabled?: boolean;
    isBiometryDisabled?: boolean;
    parentStyleNames?: string[];
};

/**
 * On-screen numeric keypad for the passcode input. Presentational only — the owning widget
 * keeps the value and decides what a key press does.
 */
export const PasscodeKeypadView = ({
    version,
    uid,
    onDigit,
    onBackspace,
    backspaceLabel,
    showBiometry,
    onBiometry,
    biometryLabel,
    isDigitsDisabled,
    isBackspaceDisabled,
    isBiometryDisabled,
    parentStyleNames,
}: TPasscodeKeypadViewProps) => {
    const digitStyleProps = getDigitStyleProps(getSemanticColors(version));
    const renderKey = (key: TKeypadKey, index: number) => {
        if (key === null) {
            return showBiometry ? (
                <ButtonCircleView
                    key={index}
                    version={version}
                    widget={{ uid: uid + '_biometry', data: {}, options: {} } as TButtonCircleViewProps['widget']}
                    label={biometryLabel}
                    iconName={AssetsSvgMap.ic_face_id}
                    size="large"
                    hierarchy="secondary"
                    raised
                    iconWidth={ICON_SIZE}
                    iconHeight={ICON_SIZE}
                    parentStyleNames={parentStyleNames}
                    isDisabled={isBiometryDisabled}
                    onClick={onBiometry}
                />
            ) : (
                <div key={index} className={styles.placeholder} />
            );
        }

        if (key === 'backspace') {
            return (
                <ButtonCircleView
                    key={index}
                    version={version}
                    widget={{ uid: uid + '_backspace', data: {}, options: {} } as TButtonCircleViewProps['widget']}
                    label={backspaceLabel}
                    iconName={AssetsSvgMap.ic_arrow_back}
                    size="large"
                    hierarchy="secondary"
                    raised
                    iconWidth={ICON_SIZE}
                    iconHeight={ICON_SIZE}
                    isDisabled={isBackspaceDisabled}
                    parentStyleNames={parentStyleNames}
                    onClick={onBackspace}
                />
            );
        }

        return (
            <CoreButtonView
                key={index}
                uid={uid + '_key_' + key}
                widget={{ uid: uid + '_key_' + key, data: { text: key }, options: {} } as TCoreButtonViewProps['widget']}
                text={key}
                styleName="passcodeKey"
                parentStyleNames={parentStyleNames}
                styleProps={digitStyleProps.button}
                isDisabled={isDigitsDisabled}
                onClick={() => onDigit(key)}
            >
                <CoreTextView
                    text={key}
                    as="span"
                    styleName="passcodeKeyText"
                    parentStyleNames={parentStyleNames}
                    isDisabled={isDigitsDisabled}
                    styleProps={digitStyleProps.text}
                />
            </CoreButtonView>
        );
    };

    return (
        <div className={styles.keypad} data-test={uid}>
            {KEYPAD_ROWS.map((row, rowIndex) => (
                <div key={rowIndex} className={styles.row}>
                    {row.map(renderKey)}
                </div>
            ))}
        </div>
    );
};
